import { Link } from "react-router-dom";
import {
  Newspaper,
  Trophy,
  Graph,
  Broadcast,
  Storefront,
  MusicNote,
  UserPlus,
  Envelope,
  ArrowUpRight,
  SealCheck,
  Eye,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import {
  currentIssue,
  pastIssues,
  artists,
  calls,
  collaborations,
  collabTypeLabels,
  collabTypeColor,
  liveShows,
  products,
  tracksByArtist,
} from "@/data/mockData";

function getArt(slug) {
  return artists.find((a) => a.slug === slug);
}
function getCallMock(id) {
  return calls.find((c) => c.id === id);
}
function getCollab(id) {
  return collaborations.find((c) => c.id === id);
}
function getProd(id) {
  return products.find((p) => p.id === id);
}
function getTrack(id) {
  for (const [slug, data] of Object.entries(tracksByArtist)) {
    const t = data.tracks.find((x) => x.id === id);
    if (t) return { ...t, artist: getArt(slug), album: data };
  }
  return null;
}
function fmt(n) {
  return n.toLocaleString("es");
}

export default function News() {
  const i = currentIssue;

  return (
    <Layout wide>
      <div data-testid="news-page">
        {/* Masthead */}
        <header className="p-4 md:p-8 border-b border-[#27272A]">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.3em]">
              <Newspaper size={12} weight="fill" className="text-[#FF3E00]" />
              <span className="text-[#FF3E00]">BOLETÍN SEMANAL</span>
              <span className="text-[#525252]">· EDICIÓN N° {String(i.number).padStart(3, "0")}</span>
              <span className="text-[#525252]">· {i.date}</span>
            </div>
            <button
              data-testid="subscribe-newsletter"
              className="btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] bg-[#FF3E00] text-white px-4 py-2 font-bold inline-flex items-center gap-2"
            >
              <Envelope size={12} weight="fill" /> Suscribirme al boletín
            </button>
          </div>
          <h1 className="font-display text-5xl md:text-8xl font-black tracking-tighter uppercase leading-[0.9]">
            Esta semana
            <br />
            <span className="text-[#FF3E00]">en el ecosistema</span>.
          </h1>
          <p className="font-display italic text-lg md:text-xl text-[#A1A1AA] mt-5 max-w-3xl leading-relaxed">
            “{i.sections.editorial}”
          </p>

          {/* Numbers of the week */}
          <div className="mt-8 grid grid-cols-3 md:grid-cols-6 border border-[#27272A]">
            <NumCell n={`€${(i.sections.number.prizesWon / 1000).toFixed(0)}K`} label="EN PREMIOS" color="#FF3E00" />
            <NumCell n={i.sections.number.newCollabs} label="NUEVAS COLABS" divider color="#00FF66" />
            <NumCell n={`${(i.sections.number.liveViewers / 1000).toFixed(1)}K`} label="VIEWERS LIVE" divider />
            <NumCell n={i.sections.number.newArtists} label="ARTISTAS NUEVOS" divider color="#FFD600" />
            <NumCell n={i.sections.number.tracksPublished} label="TRACKS PUBLIC." divider />
            <NumCell n={i.sections.number.worksSold} label="OBRAS VENDIDAS" divider color="#FF0033" />
          </div>
        </header>

        {/* Featured story */}
        <article className="border-b border-[#27272A]">
          <Link
            to={i.sections.featured.link}
            data-testid="featured-story"
            className="block relative aspect-[16/9] md:aspect-[21/9] overflow-hidden group"
          >
            <img
              src={i.sections.featured.cover}
              alt={i.sections.featured.title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-10">
              <div className="flex items-center gap-2 flex-wrap mb-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] bg-[#FF3E00] text-white px-2 py-1">
                  ⭐ PORTADA
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] bg-black/70 backdrop-blur-sm border border-white/20 text-white px-2 py-1">
                  {i.sections.featured.kind}
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-6xl font-black tracking-tighter uppercase leading-[1.02] max-w-4xl">
                {i.sections.featured.title}
              </h2>
              <p className="font-display text-base md:text-xl text-[#E5E5E5] mt-4 max-w-3xl leading-relaxed">
                {i.sections.featured.body}
              </p>
              <div className="mt-6 flex items-center gap-3">
                {i.sections.featured.artists.map((s) => {
                  const a = getArt(s);
                  return (
                    <div key={s} className="flex items-center gap-2">
                      <img
                        src={a.avatar}
                        alt={a.name}
                        className="h-9 w-9 object-cover border border-white/30"
                      />
                      <span className="font-display font-bold text-sm">{a.name}</span>
                    </div>
                  );
                })}
                <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] text-white inline-flex items-center gap-1 opacity-70 group-hover:opacity-100">
                  Leer → <ArrowUpRight size={12} />
                </span>
              </div>
            </div>
          </Link>
        </article>

        {/* SECTIONS */}
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Wins */}
          <Section icon={Trophy} title="Premios de la semana" color="#FFD600">
            <ul className="space-y-2">
              {i.sections.wins.map((w) => {
                const a = getArt(w.artist);
                const c = getCallMock(w.call);
                return (
                  <li
                    key={w.call}
                    className="grid grid-cols-[auto_1fr_auto] gap-3 items-center p-3 border border-[#27272A] bg-[#0a0a0a]"
                  >
                    <img src={a.avatar} alt={a.name} className="h-11 w-11 object-cover border border-[#27272A]" />
                    <div className="min-w-0">
                      <div className="font-display font-bold text-sm truncate flex items-center gap-1">
                        {a.name}
                        {a.verified ? <SealCheck size={11} weight="fill" className="text-[#FF3E00]" /> : null}
                      </div>
                      <Link
                        to={`/convocatorias/${c.id}`}
                        className="font-mono text-[10px] text-[#A1A1AA] hover:text-white tracking-wider truncate block"
                      >
                        ↪ {w.label.toUpperCase()}
                      </Link>
                    </div>
                    <div className="font-display font-black text-[#FFD600] text-lg">
                      {w.amount}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Section>

          {/* New collabs */}
          <Section icon={Graph} title="Nuevas colabs registradas" color="#FF3E00">
            <ul className="space-y-2">
              {i.sections.newCollabs.map((nc) => {
                const c = getCollab(nc.id);
                const a = getArt(c.from);
                const b = getArt(c.to);
                const color = collabTypeColor[c.type];
                return (
                  <li
                    key={nc.id}
                    className="flex items-center gap-3 p-3 border border-[#27272A] bg-[#0a0a0a]"
                  >
                    <div className="flex -space-x-2 shrink-0">
                      <img src={a.avatar} alt="" className="h-9 w-9 object-cover border-2 border-[#050505]" />
                      <img src={b.avatar} alt="" className="h-9 w-9 object-cover border-2 border-[#050505]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-sm truncate">
                        {a.name} × {b.name}
                      </div>
                      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider truncate">
                        {c.label} · {c.year}
                      </div>
                    </div>
                    <span
                      className="font-mono text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5 shrink-0"
                      style={{ background: color, color: "#050505" }}
                    >
                      {collabTypeLabels[c.type]}
                    </span>
                  </li>
                );
              })}
            </ul>
            <Link
              to="/colaboraciones"
              className="mt-3 block text-center font-mono uppercase text-[10px] tracking-[0.22em] border border-[#27272A] hover:border-[#FF3E00] hover:text-[#FF3E00] py-2 text-[#A1A1AA]"
            >
              Ver mapa completo →
            </Link>
          </Section>

          {/* Top live */}
          <Section icon={Broadcast} title="Live más visto" color="#FF0033">
            {(() => {
              const live = liveShows.find((l) => l.id === i.sections.topLive.id);
              const a = getArt(i.sections.topLive.artist);
              return (
                <Link
                  to={`/live/${live.id}`}
                  className="block relative aspect-video overflow-hidden group"
                >
                  <img
                    src={live.cover}
                    alt={live.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.22em] bg-[#FF0033] text-white px-2 py-1">
                    <span className="h-1 w-1 bg-white live-dot" /> RECORD
                  </div>
                  <div className="absolute top-3 right-3 font-mono text-[10px] tracking-wider bg-black/70 text-white px-2 py-1 inline-flex items-center gap-1">
                    <Eye size={11} weight="fill" /> {fmt(i.sections.topLive.viewers)}
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-4">
                    <div className="font-mono text-[10px] text-[#FF3E00] tracking-wider">
                      {live.discipline}
                    </div>
                    <div className="font-display font-black text-xl tracking-tight">
                      {i.sections.topLive.title}
                    </div>
                    <div className="font-mono text-[10px] text-[#E5E5E5] tracking-wider mt-1 flex items-center gap-2">
                      <img src={a.avatar} alt="" className="h-5 w-5 object-cover" />
                      {a.name} · {i.sections.topLive.venue.toUpperCase()}
                    </div>
                  </div>
                </Link>
              );
            })()}
          </Section>

          {/* Top prints */}
          <Section icon={Storefront} title="Más vendidos" color="#00FF66">
            <ul className="space-y-2">
              {i.sections.topPrints.map((tp, idx) => {
                const p = getProd(tp.id);
                return (
                  <li key={tp.id}>
                    <Link
                      to={`/tienda/${p.id}`}
                      className="flex items-center gap-3 p-3 border border-[#27272A] bg-[#0a0a0a] hover:border-[#00FF66] group"
                    >
                      <span className="font-mono text-xl font-bold text-[#00FF66] tracking-wider shrink-0 w-6">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <img
                        src={p.image}
                        alt=""
                        className="h-11 w-11 object-cover border border-[#27272A] grayscale group-hover:grayscale-0 transition-all"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-sm truncate">
                          {p.title}
                        </div>
                        <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                          @{p.artist.handle} · {tp.sold} ventas esta semana
                        </div>
                      </div>
                      <span className="font-display font-black text-[#FF3E00] text-sm whitespace-nowrap">
                        {p.currency === "ETH" ? `Ξ${p.price}` : `€${p.price}`}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </Section>

          {/* Trending tracks */}
          <Section icon={MusicNote} title="Tracks en ascenso" color="#A78BFA">
            <ul className="space-y-2">
              {i.sections.trendingTracks.map((t) => {
                const track = getTrack(t.id);
                if (!track) return null;
                return (
                  <li
                    key={t.id}
                    className="flex items-center gap-3 p-3 border border-[#27272A] bg-[#0a0a0a]"
                  >
                    <img src={track.album.cover} alt="" className="h-11 w-11 object-cover border border-[#27272A]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-sm truncate">
                        {track.title}
                      </div>
                      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider truncate">
                        {track.artist.name} · {fmt(track.plays)} plays
                      </div>
                    </div>
                    <span className="font-mono text-xs font-bold text-[#00FF66] whitespace-nowrap">
                      ↑ {t.delta}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Section>

          {/* Debuts */}
          <Section icon={UserPlus} title="Debuts del ecosistema" color="#0033FF">
            <ul className="space-y-2">
              {i.sections.debuts.map((d) => {
                const a = getArt(d.artist);
                return (
                  <li
                    key={d.artist}
                    className="flex items-center gap-3 p-3 border border-[#27272A] bg-[#0a0a0a]"
                  >
                    <img src={a.avatar} alt={a.name} className="h-11 w-11 object-cover border border-[#27272A]" />
                    <div className="flex-1 min-w-0">
                      <Link to={`/artista/${a.slug}`} className="font-display font-bold text-sm hover:text-[#FF3E00]">
                        {a.name}
                      </Link>
                      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider truncate">
                        {d.reason}
                      </div>
                    </div>
                    <span className="font-mono text-[9px] uppercase tracking-[0.22em] bg-[#0033FF] text-white px-1.5 py-0.5 shrink-0">
                      DEBUT
                    </span>
                  </li>
                );
              })}
            </ul>
          </Section>
        </div>

        {/* Newsletter signup */}
        <section className="border-t border-[#27272A] p-6 md:p-10 bg-gradient-to-br from-[#0a0a0a] to-[#050505]">
          <div className="max-w-3xl mx-auto text-center">
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-3">
              CADA DOMINGO · 08:00
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter uppercase leading-[0.95]">
              El ecosistema
              <br />
              <span className="text-[#FF3E00]">en tu inbox</span>.
            </h2>
            <p className="font-mono text-sm text-[#A1A1AA] tracking-wider mt-5 max-w-xl mx-auto">
              {fmt(i.subscribers)} ARTISTAS Y CURADORES LO LEEN · {i.openRate}% TASA DE APERTURA · SIN SPAM
            </p>
            <form
              onSubmit={(e) => e.preventDefault()}
              className="mt-8 flex flex-col sm:flex-row gap-2 max-w-xl mx-auto"
            >
              <input
                data-testid="newsletter-email"
                type="email"
                placeholder="tucorreo@dominio.com"
                className="flex-1 bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none px-4 py-3 font-mono text-sm"
              />
              <button
                data-testid="newsletter-subscribe"
                className="btn-brutal bg-[#FF3E00] text-white font-mono uppercase text-xs tracking-[0.22em] font-bold px-6 py-3"
              >
                Suscribirme gratis →
              </button>
            </form>
          </div>
        </section>

        {/* Past issues archive */}
        <section className="p-4 md:p-6 border-t border-[#27272A]">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-display font-black uppercase tracking-tight text-xl">
              Ediciones anteriores
            </h2>
            <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
              ARCHIVO COMPLETO
            </span>
          </div>
          <ul className="border-t border-[#27272A]">
            {pastIssues.map((p) => (
              <li
                key={p.n}
                className="grid grid-cols-[80px_90px_1fr_auto] gap-4 items-center py-3 border-b border-[#27272A] hover:bg-[#0a0a0a] cursor-pointer group"
              >
                <span className="font-mono text-[10px] text-[#525252] tracking-wider">
                  N° {String(p.n).padStart(3, "0")}
                </span>
                <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider uppercase">
                  {p.date}
                </span>
                <span className="font-display font-bold group-hover:text-[#FF3E00]">
                  {p.headline}
                </span>
                <ArrowUpRight size={14} className="text-[#A1A1AA] group-hover:text-[#FF3E00]" />
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Layout>
  );
}

function NumCell({ n, label, divider, color }) {
  return (
    <div
      className={`p-3 md:p-4 text-center ${
        divider ? "border-l border-t md:border-t-0 border-[#27272A]" : ""
      }`}
    >
      <div
        className="font-display font-black text-xl md:text-2xl tracking-tight"
        style={color ? { color } : {}}
      >
        {n}
      </div>
      <div className="font-mono text-[8px] md:text-[9px] text-[#A1A1AA] tracking-[0.2em] mt-1">
        {label}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, color, children }) {
  return (
    <section className="p-4 md:p-6 border-b lg:border-b-0 lg:[&:nth-child(2n)]:border-l [&:nth-child(n+3)]:lg:border-t border-[#27272A]">
      <header className="flex items-center gap-2 mb-4">
        <Icon size={16} weight="fill" style={{ color }} />
        <h3 className="font-display font-black uppercase tracking-tight text-lg">
          {title}
        </h3>
      </header>
      {children}
    </section>
  );
}
